<script setup lang="ts">
import { ref } from "vue";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import Button from "primevue/button";
import type { CreateActionDTO } from "src/api/models/createActionDTO";
import { CreateActionDTOType } from "src/api/models/createActionDTOType";

const props = defineProps<{
  onSubmit: (data: CreateActionDTO) => Promise<void>;
}>();

const visible = defineModel<boolean>("visible", { default: false });

const typeOptions = Object.values(CreateActionDTOType).map((value) => ({
  label: value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  value,
}));

const form = ref({ name: "", price: 0, type: "" });
const submitting = ref(false);
const error = ref<string | null>(null);

function resetForm() {
  form.value = { name: "", price: 0, type: "" };
  error.value = null;
}

async function handleSubmit() {
  if (!form.value.name.trim() || !form.value.type) {
    error.value = "Name and type are required";
    return;
  }
  submitting.value = true;
  error.value = null;
  try {
    await props.onSubmit({
      name: form.value.name.trim(),
      price: form.value.price,
      type: form.value.type as CreateActionDTO["type"],
    });
    visible.value = false;
    resetForm();
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : "Failed to create action";
  } finally {
    submitting.value = false;
  }
}

function onHide() {
  resetForm();
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    header="Create Action"
    :modal="true"
    :style="{ width: '480px' }"
    @hide="onHide"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Name</label>
        <InputText v-model="form.name" placeholder="Action name" autofocus :disabled="submitting" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Price</label>
        <InputNumber
          v-model="form.price"
          mode="currency"
          currency="EUR"
          locale="nl-NL"
          :min="0"
          :disabled="submitting"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Type</label>
        <Select
          v-model="form.type"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          placeholder="Select a type"
          :disabled="submitting"
        />
      </div>

      <small v-if="error" class="text-red-500">{{ error }}</small>
    </div>

    <template #footer>
      <Button label="Cancel" severity="secondary" text :disabled="submitting" @click="visible = false" />
      <Button
        label="Create & Link"
        icon="pi pi-check"
        :loading="submitting"
        @click="handleSubmit"
      />
    </template>
  </Dialog>
</template>
